import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Heatmap from './Heatmap/Heatmap';
import { HeatmapLegend } from './Heatmap/HeatmapLegend';
import { useDebouncedResizeObserver, useSorting } from '~/shared/hooks';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import isEqual from 'lodash.isequal';
import { downloadHeatmapData } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import FilterBar from './FilterBar';
import { Flex, AlertBar, Loader } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorStackedBarChart from './Indicator/IndicatorStackedBarChart';
import IndicatorTable from './Indicator/IndicatorTable';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import MapTabs from './Heatmap/MapTabs';

const ScrollWrap = styled.div`
  position: relative;
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  @media (max-width: 1600px) {
    max-width: 100%;
  }
  padding: 20px !important;
`;

const MainContainer = styled(Paper)<{ $gridheight: number }>`
  position: relative;
  max-width: 100%;
  margin-left: 2px;
  padding-top: 100px;
  height: ${({ $gridheight }) => ($gridheight ? `${$gridheight * 100}px` : '100%')};
  max-height: ${({ $gridheight }) => ($gridheight ? `${$gridheight * 100}px` : '100%')};
  display: flex;
  flex-direction: column;
  background: #fff !important;
  box-shadow: none !important;
  .MuiFormControl-root {
    justify-content: center;
  }
`;

const ContentContainerInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
`;

const Header = styled(Flex)`
  position: absolute;
  z-index: 2;
  left: 6px;
  top: 24px;
  color: var(--color-chart-header);
  font-size: 20px;
  font-weight: 500;
  width: 100%;
  align-items: center;
  padding: 0 0 0 32px;
  min-height: 93px;
  overflow: visible;
`;

const HeatmapLegendWrapper = styled.div`
  padding: 0 8px;
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  margin-top: 12px;
  min-width: calc(50% - 12px);
  ${({ $iswide }) => $iswide === 1 && 'flex: 1;'}
`;

const ContentContainer = styled.div<{ width: number; $isgrid: number; $gridheight: number }>`
  background: white;
  height: 100%;
  display: flex;
  .content {
    display: flex;
    padding: 24px 24px 24px 0 !important;
  }
  #heatmap-subtitle {
    margin-left: 24px !important;
    margin-top: 0px !important;
  }
  #heatmap-ytitle {
    margin-left: 12px !important;
    margin-top: ${({ $gridheight }) => ($gridheight ? `${$gridheight * 50}px` : '0')} !important;
  }
  ${({ $isgrid, width }) => !$isgrid && `width: ${width > 1200 ? width : 1200}px;`}
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  flex-direction: column;
  padding: 16px 36px 32px 8px;
`;

const HeatmapSubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 24px);
  height: 100%;
  position: relative;
`;

const HeatmapYtitle = styled.div`
  width: 24px;
  height: 24px;
  text-align: center;
  transform: rotate(270deg);
  font-size: 14px;
`;

const HeatmapSubtitle = styled.div`
  width: 100%;
  height: 24px;
  text-align: left;
  font-size: 16px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: white;
  }
`;

const RelativeContainer = styled.div`
  position: relative;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
`;

const AlignedLoader = styled(Loader)`
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;

// eslint-disable-next-line @typescript-eslint/ban-types
function useHeatmapClick(navHirarchyItem: any, onScroll: Function, filterValues: any, data: any) {
  const [heatmapIndicator, setHeatmapIndicator] = useDynamicRecoil('heatmapIndicator', undefined);
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  const [oldnavHirarchyItem, setOldnavHirarchyItem] = useState<any>();

  useEffect(() => {
    if (!isEqual(navHirarchyItem, oldnavHirarchyItem)) {
      setOldnavHirarchyItem(navHirarchyItem);
      setIndicatorSections([]);
      setHeatmapIndicator([]);
    }
  }, [navHirarchyItem, filterValues, oldnavHirarchyItem]);

  useEffect(() => {
    if (heatmapIndicator && isEqual(indicatorSections, [])) setIndicatorSections(heatmapIndicator);
  }, [heatmapIndicator]);

  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setHeatmapIndicator([]);
      setTimeout(
        () =>
          setIndicatorSections(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return {
                indicator,
                cellInfo: {
                  ...cellInfo,
                  topic:
                    data && data?.row_nodes && data?.row_nodes?.filter((r: any) => r.name === cellInfo?.topic)[0]?.id,
                  symbol:
                    data && data?.col_nodes && data?.col_nodes?.filter((r: any) => r.name === cellInfo?.symbol)[0]?.id,
                },
                type,
              };
            }),
          ),
        100,
      );
      setTimeout(
        () =>
          setHeatmapIndicator(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return {
                indicator,
                cellInfo: {
                  ...cellInfo,
                  topic:
                    data && data?.row_nodes && data?.row_nodes?.filter((r: any) => r.name === cellInfo?.topic)[0]?.id,
                  symbol:
                    data && data?.col_nodes && data?.col_nodes?.filter((r: any) => r.name === cellInfo?.symbol)[0]?.id,
                },
                type,
              };
            }),
          ),
        120,
      );
      setTimeout(onScroll, 400);
    },
    [navHirarchyItem, onScroll, data],
  );

  return { indicatorSections, heatmapIndicator, onClick };
}

const tabOptions = [
  {
    label: 'heatmap',
    value: 'heatmap',
  },
  {
    label: 'map',
    value: 'map',
  },
];

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};

export default function HeatmapPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  onGridClick,
  gridFilterValues,
  gridSorting,
  onGridSortingChange,
  gridInitialSorting,
  gridFilterValuesID,
}: any) {
  const [, setSelectedChartDisplay] = useDynamicRecoil('selectedChartDisplay', undefined);
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();

  const [error, setError] = useState<any>(null);

  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  useEffect(() => {
    setSelectedChartDisplay(navHirarchyItem?.displayName || navHirarchyItem?.name);
  }, [navHirarchyItem]);

  const {
    data = undefined,
    colorScheme,
    error: dataError,
    isLoading = true,
    chartTitle,
    chartSubtitle,
    chartYtitle,
  } = useHeatmapData(category, subcategory, subsubcategory, prism, gridFilterValuesID || filterValuesID, prismId);

  const showRank = useMemo(() => data?.col_nodes?.map((cn: any) => cn.id)?.includes('order'), [data]);
  const { sorting, onSortingChange, initialSorting, initialRankSorting, setSorting } = useSorting(showRank);

  useEffect(() => {
    setSorting(showRank ? initialRankSorting : initialSorting);
    // Surface which heatmap dashboards request rank sorting so we can align Storybook coverage.
    console.info('Heatmap sort mode', {
      prismId,
      dashboard: navHirarchyItem?.name,
      showRank,
    });
  }, [prismId, showRank]);

  const [dataChanged, setDataChanged] = useState<boolean>(false);

  useEffect(() => {
    setDataChanged(true);
    setTimeout(() => setDataChanged(false), 5);
  }, [prism]);

  const [activeTab, setActiveTab] = useState('heatmap');
  // Temporary variable. Later we should check if data contains states and in such case we show tabs
  const isStates = false;

  const size = useWindowSize();

  const { size: heatmapSize, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);

  const [calculatedHeatmapSize, setCalculatedHeatmapSize] = useState(heatmapSize);

  useEffect(() => {
    setCalculatedHeatmapSize({
      width: gridHeight ? heatmapSize.width : heatmapSize.width + 30,
      height: heatmapSize.height,
    });
  }, [heatmapSize]);

  const { indicatorSections, onClick } = useHeatmapClick(
    navHirarchyItem,
    scrollToBottom,
    gridFilterValues || filterValues,
    data,
  );

  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
    if (isLoading) {
      setError(null);
    }
  }, [dataError, isLoading]);

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      gridFilterValuesID || filterValuesID,
      prismId,
      chartTitle,
    ).catch(e => {
      alert(e.message);
    });

  const handleOnGridClick = useCallback(
    (props: any) => {
      onGridClick({
        ...props,
        navHirarchyItem,
        filterValues: gridFilterValues || filterValues,
        category,
        subcategory,
        subsubcategory,
        prism,
        prismId,
        indicatorSections,
      });
    },
    [
      navHirarchyItem,
      filterValues,
      gridFilterValues,
      category,
      subcategory,
      subsubcategory,
      prism,
      prismId,
      indicatorSections,
      onGridClick,
    ],
  );

  const [refreshFilterBar, setRefreshFilterBar] = useState(false);

  useEffect(() => {
    setRefreshFilterBar(true);
    setTimeout(() => setRefreshFilterBar(false), 10);
  }, [location?.pathname]);

  const type = useMemo(() => navHirarchyItem?.type, [navHirarchyItem]);
  const filters = useMemo(() => navHirarchyItem?.filters, [navHirarchyItem]);
  const filterHierarchy = useMemo(() => navHirarchyItem?.narrativeFilterTree, [navHirarchyItem]);
  const dateType = useMemo(() => filters?.[filters?.length - 1 || 0]?.type, [filters]);

  return (
    <ScrollWrap className="heatmapPage">
      {error && <AlertBar message={error.message} />}
      {isLoading && (
        <LoaderWrapper>
          <AlignedLoader />
        </LoaderWrapper>
      )}
      <MainContainer id="maincontainer" $gridheight={gridHeight || 6}>
        {!gridIndex && gridIndex !== 0 && (
          <Header>
            {refreshFilterBar ? (
              <div />
            ) : (
              <FilterBar
                type={type}
                dateType={dateType}
                filters={filters}
                values={filterValues}
                filterHierarchy={filterHierarchy}
                filterHierarchyValues={filterValuesID}
                initialSorting={gridIndex ? gridInitialSorting : showRank ? initialRankSorting : initialSorting}
                onDownload={onDownload}
                onSortingChange={onSortingChange}
                tooltipCfg={tooltipCfg}
                isHeatmap
                isGrid={gridIndex || gridIndex === 0}
                showRank={showRank}
                prismId={prismId}
              />
            )}
          </Header>
        )}
        {isStates && (
          <MapTabs options={tabOptions} value={activeTab} onChange={(_, newTabValue) => setActiveTab(newTabValue)} />
        )}
        <ContentContainer width={size.width - 328} $isgrid={gridHeight ? 1 : 0} $gridheight={gridHeight}>
          <ContentContainerInner className="content">
            <HeatmapYtitle id="heatmap-ytitle">{chartYtitle}</HeatmapYtitle>
            <HeatmapSubtitleContainer>
              <HeatmapSubtitle id="heatmap-subtitle">{chartSubtitle}</HeatmapSubtitle>
              <HeatmapContainer ref={chartSizeRef}>
                <RelativeContainer>
                  {!dataChanged && (
                    <Heatmap
                      {...{
                        data,
                        size: calculatedHeatmapSize,
                        onClick: gridIndex || gridIndex === 0 ? handleOnGridClick : onClick,
                        sorting: gridIndex ? gridSorting : sorting,
                        colorScheme,
                        onSortingChange: gridIndex ? onGridSortingChange : onSortingChange,
                        tooltipCfg,
                        gridHeight,
                        gridIndex: gridIndex || 0,
                      }}
                    />
                  )}
                </RelativeContainer>
              </HeatmapContainer>
            </HeatmapSubtitleContainer>
            <HeatmapLegendWrapper>
              <HeatmapLegend size={heatmapSize} data={data} colorScheme={colorScheme} />
            </HeatmapLegendWrapper>
          </ContentContainerInner>
        </ContentContainer>
      </MainContainer>
      <IndicatorsContainer ref={gridIndex || gridIndex === 0 ? undefined : scrollerRef}>
        {!gridIndex &&
          gridIndex !== 0 &&
          indicatorSections.map((section: any, index: number) => (
            <IndicatorContainer
              key={'container' + section.indicator + section.topic + section.symbol + String(index)}
              $iswide={indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0 ? 1 : 0}
            >
              {section.type === 'bar' && (
                <IndicatorStackedBarChart
                  isHeatmap
                  isFirst={index % 2 !== 0}
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
                ></IndicatorStackedBarChart>
              )}
              {section.type === 'line' && (
                <IndicatorLineChart
                  isHeatmap
                  isFirst={index % 2 !== 0}
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
                ></IndicatorLineChart>
              )}
              {section.type === 'table' && (
                <IndicatorTable
                  isHeatmap
                  isFirst={index % 2 === 0}
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
                ></IndicatorTable>
              )}
            </IndicatorContainer>
          ))}
      </IndicatorsContainer>
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
